import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import LoginModal from '@components/Auth/LoginModal';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { NavHeader } from '@molecules/index';
import {
    getAuth,
    removeAppRedirection,
    removeAuth,
    removeReferralCode,
    removeReferralEntityType,
} from '@services/identity.service';
import { logoutUser } from '@services/users.service';
import FooterV2 from '@molecules/Footer/V2/Footer';
import useScrollToTopOnRouteChange from '@ui/hooks/useScrollToTopOnRouteChange';
import { useInvestorContext } from '@context/InvestorContextProvider';
import useBroadcastChannel from 'hooks/useBroadcastChannel';
import {
    getFestivalConfiguration,
    listenBroadCastMessage,
    reloadAuthenticatedTabs,
} from '@helpers/miscelleanous';
import BottomNavigation from '@molecules/BottomNavigation';
import bottomNavigationTexts from "@molecules/BottomNavigation/en.json";
import 'aos/dist/aos.css';
import { getIdleLogoutTime } from '@helpers/constants';

library.add(fas);

const StaticLayout = ({
    children,
    usePlainBackground = false,
    ...pageProps
}) => {
    useScrollToTopOnRouteChange();
    const router = useRouter();
    useBroadcastChannel('logout-event', (log) => {
        // Clear user-related data, redirect to the login page, etc.
        listenBroadCastMessage(log, 'logout', router);
    });
    const [showLogin, setShowLogin] = useState(false);
    const [showSignUp, setShowSignUp] = useState(false);
    const [showEmailInput, setShowEmailInput] = useState(false);
    const { menu, menuLoading, investorCache } = useInvestorContext();
    const [festiveSeason, setFestiveSeason] = useState(null);
    const showBottomNavigation = bottomNavigationTexts?.hideBottomNavOnFirstLevelPages.indexOf(router?.pathname) === -1;
    const [isBottomNavVisible, setIsBottomNavVisible] = useState(false);

    const handleMenuClicked = (menu) => {
        const actions = {
            Login: () => setShowLogin(true),
            SignUp: () => {
                setShowLogin(true);
                setShowEmailInput(true);
                setShowSignUp(true);
            },
        };
        const handler = actions[menu];
        if (handler) handler();
    };

    const handleLoginModalClose = () => {
        setShowLogin(false);
        setShowEmailInput(false);
        setShowSignUp(false);
    };

    const logout = async () => {
        await logoutUser(getAuth());
        removeReferralCode();
        removeReferralEntityType();
        removeAppRedirection();
        removeAuth();
        router.push('/');
        reloadAuthenticatedTabs();
    };

    // code for auto-logging out after 2 hrs of inactivity
    const debounceRef = useRef();
    const time = getIdleLogoutTime();

    debounceRef.current = (function debounce() {
        let timerId;

        return function () {
            if (timerId) {
                clearTimeout(timerId);
            }

            timerId = setTimeout(() => {
                if (getAuth()) {
                    logout();
                }
            }, time);
        };
    })();

    function tracker() {
        window.addEventListener('mousemove', debounceRef.current);
        window.addEventListener('scroll', debounceRef.current);
        window.addEventListener('keydown', debounceRef.current);
    }

    useEffect(() => {
        debounceRef.current();
        tracker();
        (async () => {
                const festiveSeason = await getFestivalConfiguration();
                setFestiveSeason(festiveSeason);
        })();
    }, []);

    const childrenWithProps = React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, { ...pageProps, festiveSeason });
        }
        return child;
    });

    const whiteBgPages = ["/ai-safe", "/faq", "/contact-us", "/careers", "/credit-evaluation-process", "/referandearn"]

    return (
        <div className={`homepage-wrapper ${isBottomNavVisible ? 'pb-[70px] xl:pb-0' : 'pb-0'} ${whiteBgPages.includes(router.pathname) ? 'bg-basicWhite' : 'bg-gray-50'}`}>
            <NavHeader
                hideHeaderOnScroll={true}
                menuClicked={handleMenuClicked}
                homePage={true}
                menu={menu}
                menuLoading={menuLoading}
                investorCache={investorCache}
                festiveSeason={festiveSeason}
            />
            {(showLogin || showSignUp) && (
                <LoginModal
                    show={showLogin}
                    showLogin={showLogin}
                    showSignUp={showSignUp}
                    initialShowEmailInput={showEmailInput}
                    onClose={handleLoginModalClose}
                    setShowSignUp={setShowSignUp}
                    setShowLogin={setShowLogin}
                />
            )}
            <div
                className={`relative ${router?.pathname?.includes('/create-new-account')
                    ? 'overflow-visible'
                    : 'overflow-hidden'
                    } text-left ${router.pathname !== '/about-us' &&
                    router.pathname !== '/credit-evaluation-process' &&
                    router.pathname !== '/contact-us' &&
                    'page-wrapper'
                    }`}
            >
                {childrenWithProps}
            </div>

            <FooterV2 className="px-4 md:px-0 2xl:px-36" />
            {investorCache && showBottomNavigation ? <BottomNavigation investorCache={investorCache} visible={true} setIsBottomNavVisible={setIsBottomNavVisible} /> : null}
        </div>
    );
};

StaticLayout.propTypes = {
    children: PropTypes.node,
};

export default StaticLayout;
